import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { getModelDisplayName } from '../../constants/models';

// Model colors
const MODEL_COLORS = ['#00BCD4', '#32CD32', '#F59E0B', '#8B5CF6', '#EC4899', '#10B981'];

/**
 * ComparisonTokenChart - Displays token usage by model
 * Shows average tokens per model from comparison data
 */
const ComparisonTokenChart = ({ comparisons, loading }) => {
  // Aggregate token data by model
  const { chartData, totalTokens } = useMemo(() => {
    if (!comparisons || comparisons.length === 0) {
      return { chartData: [], totalTokens: 0 };
    }

    const modelStats = {};
    let total = 0;

    comparisons.forEach(comp => {
      // Process model 1
      if (comp.model_1 && comp.token_usage_1) {
        if (!modelStats[comp.model_1]) {
          modelStats[comp.model_1] = { tokens: [], count: 0 };
        }
        modelStats[comp.model_1].count++;
        const tokens = comp.token_usage_1.total_tokens || 0;
        if (tokens > 0) {
          modelStats[comp.model_1].tokens.push(tokens);
          total += tokens;
        }
      }

      // Process model 2
      if (comp.model_2 && comp.token_usage_2) {
        if (!modelStats[comp.model_2]) {
          modelStats[comp.model_2] = { tokens: [], count: 0 };
        }
        modelStats[comp.model_2].count++;
        const tokens = comp.token_usage_2.total_tokens || 0;
        if (tokens > 0) {
          modelStats[comp.model_2].tokens.push(tokens);
          total += tokens;
        }
      }
    });

    // Calculate averages and format for chart
    const data = Object.entries(modelStats)
      .map(([model, stats]) => {
        const avgTokens = stats.tokens.length > 0
          ? stats.tokens.reduce((a, b) => a + b, 0) / stats.tokens.length
          : 0;

        const displayName = getModelDisplayName(model);
        const shortName = displayName.length > 10 ? displayName.substring(0, 8) + '..' : displayName;

        return {
          model: shortName,
          fullModel: displayName,
          avgTokens: Math.round(avgTokens),
          count: stats.count
        };
      })
      .filter(d => d.avgTokens > 0)
      .sort((a, b) => b.avgTokens - a.avgTokens)
      .slice(0, 6);

    return {
      chartData: data,
      totalTokens: total
    };
  }, [comparisons]);

  // Format tokens
  const formatTokens = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toLocaleString();
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="rounded-lg p-3 shadow-lg bg-bg-secondary/95 border border-violet-500/30 backdrop-blur-sm">
          <p className="text-sm font-semibold mb-2 text-text-primary">
            {data.fullModel}
          </p>
          <div className="space-y-1">
            <div className="flex justify-between gap-4">
              <span className="text-xs text-text-tertiary">Avg Tokens</span>
              <span className="text-xs font-mono text-violet-500">
                {data.avgTokens.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-xs text-text-tertiary">Comparisons</span>
              <span className="text-xs font-mono text-text-secondary">
                {data.count}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };
  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array
  };

  if (loading) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary">
        <div className="h-4 w-40 rounded mb-4 animate-pulse bg-success-50"></div>
        <div className="h-[200px] rounded animate-pulse bg-primary-500/5"></div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary transition-all duration-200 hover:border-violet-500/25">
        <h3 className="text-sm font-semibold mb-4 text-text-primary">
          Tokens by Model
        </h3>
        <div className="h-[200px] flex flex-col items-center justify-center">
          <svg className="w-10 h-10 mb-2 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
          <p className="text-sm text-text-tertiary">No token data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary transition-all duration-200 hover:border-violet-500/25">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            Tokens by Model
          </h3>
          <p className="text-xs mt-0.5 text-text-tertiary">
            Average tokens per comparison
          </p>
        </div>
        <div className="text-right">
          <span className="text-sm font-bold font-mono text-violet-500">
            {formatTokens(totalTokens)}
          </span>
          <p className="text-xs text-text-tertiary">
            total
          </p>
        </div>
      </div>
      
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="rgba(50, 205, 50, 0.1)" 
              vertical={false}
            />
            <XAxis 
              dataKey="model"
              stroke="#6B8E6B"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: 'rgba(50, 205, 50, 0.2)' }}
              tick={{ fill: '#A8D5A2' }}
            />
            <YAxis 
              tickFormatter={formatTokens}
              stroke="#6B8E6B"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: 'rgba(50, 205, 50, 0.2)' }}
              tick={{ fill: '#6B8E6B' }}
              width={45}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(139, 92, 246, 0.08)' }} />
            <Bar 
              dataKey="avgTokens" 
              name="Avg Tokens"
              radius={[4, 4, 0, 0]}
              maxBarSize={50}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={MODEL_COLORS[index % MODEL_COLORS.length]}
                  style={{ filter: 'drop-shadow(0 0 4px rgba(139, 92, 246, 0.3))' }}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

ComparisonTokenChart.propTypes = {
  comparisons: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool
};

export default ComparisonTokenChart;
